'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ROICalculatorProps {
  productName: string;
}

export default function ROICalculator({ productName }: ROICalculatorProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [employees, setEmployees] = useState(100);
  const [avgSalary, setAvgSalary] = useState(50000);
  const [hoursPerWeek, setHoursPerWeek] = useState(5);
  const [results, setResults] = useState({
    timeSaved: 0,
    costSavings: 0,
    roi: 0,
    paybackMonths: 0,
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const calculateROI = () => {
      const hourlyRate = avgSalary / 2080; // Annual hours
      const weeklyTimeSaved = employees * hoursPerWeek * 0.4; // 40% efficiency gain
      const annualTimeSaved = weeklyTimeSaved * 52;
      const annualCostSavings = annualTimeSaved * hourlyRate;
      const implementationCost = employees * 150; // Mock cost per user
      const roiPercentage = ((annualCostSavings - implementationCost) / implementationCost) * 100;
      const paybackMonths = (implementationCost / (annualCostSavings / 12));

      setResults({
        timeSaved: Math.round(annualTimeSaved),
        costSavings: Math.round(annualCostSavings),
        roi: Math.round(roiPercentage),
        paybackMonths: Math.round(paybackMonths * 10) / 10,
      });
    };

    calculateROI();
  }, [employees, avgSalary, hoursPerWeek, isHydrated]);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6 border border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface rounded w-3/4"></div>
          <div className="h-32 bg-surface rounded"></div>
          <div className="h-48 bg-surface rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="flex items-center space-x-3 mb-6">
        <Icon name="CalculatorIcon" size={28} variant="solid" className="text-accent" />
        <h3 className="font-display font-bold text-2xl text-primary">
          {productName} ROI Calculator
        </h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-6">
          <div>
            <label className="flex items-center justify-between mb-2">
              <span className="font-body font-medium text-sm text-foreground">Number of Employees</span>
              <span className="font-display font-bold text-lg text-primary">{employees}</span>
            </label>
            <input
              type="range"
              min="10"
              max="1000"
              step="10"
              value={employees}
              onChange={(e) => setEmployees(Number(e.target.value))}
              className="w-full h-2 bg-surface rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between mt-1">
              <span className="font-body text-xs text-muted-foreground">10</span>
              <span className="font-body text-xs text-muted-foreground">1000</span>
            </div>
          </div>

          <div>
            <label className="flex items-center justify-between mb-2">
              <span className="font-body font-medium text-sm text-foreground">Average Annual Salary ($)</span>
              <span className="font-display font-bold text-lg text-primary">${avgSalary.toLocaleString()}</span>
            </label>
            <input
              type="range"
              min="30000"
              max="150000"
              step="5000"
              value={avgSalary}
              onChange={(e) => setAvgSalary(Number(e.target.value))}
              className="w-full h-2 bg-surface rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between mt-1">
              <span className="font-body text-xs text-muted-foreground">$30K</span>
              <span className="font-body text-xs text-muted-foreground">$150K</span>
            </div>
          </div>

          <div>
            <label className="flex items-center justify-between mb-2">
              <span className="font-body font-medium text-sm text-foreground">Hours Spent Per Week</span>
              <span className="font-display font-bold text-lg text-primary">{hoursPerWeek}h</span>
            </label>
            <input
              type="range"
              min="1"
              max="20"
              step="1"
              value={hoursPerWeek}
              onChange={(e) => setHoursPerWeek(Number(e.target.value))}
              className="w-full h-2 bg-surface rounded-lg appearance-none cursor-pointer accent-primary"
            />
            <div className="flex justify-between mt-1">
              <span className="font-body text-xs text-muted-foreground">1h</span>
              <span className="font-body text-xs text-muted-foreground">20h</span>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-primary to-brand-teal rounded-lg p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <span className="font-body text-sm opacity-90">Annual ROI</span>
              <Icon name="TrendingUpIcon" size={24} variant="solid" />
            </div>
            <p className="font-display font-bold text-4xl mb-2">{results.roi}%</p>
            <p className="font-body text-xs opacity-80">Return on Investment</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-success bg-opacity-10 rounded-lg p-4 border border-success border-opacity-20">
              <Icon name="ClockIcon" size={20} variant="solid" className="text-success mb-2" />
              <p className="font-display font-bold text-xl text-foreground mb-1">
                {results.timeSaved.toLocaleString()}h
              </p>
              <p className="font-body text-xs text-muted-foreground">Time Saved Annually</p>
            </div>

            <div className="bg-trust bg-opacity-10 rounded-lg p-4 border border-trust border-opacity-20">
              <Icon name="CurrencyDollarIcon" size={20} variant="solid" className="text-trust mb-2" />
              <p className="font-display font-bold text-xl text-foreground mb-1">
                ${results.costSavings.toLocaleString()}
              </p>
              <p className="font-body text-xs text-muted-foreground">Annual Cost Savings</p>
            </div>
          </div>

          <div className="bg-surface rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-body font-medium text-sm text-foreground">Payback Period</span>
              <span className="font-display font-bold text-lg text-accent">{results.paybackMonths} months</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-accent transition-all duration-500"
                style={{ width: `${Math.min((12 / results.paybackMonths) * 100, 100)}%` }}
              ></div>
            </div>
          </div>

          <button className="w-full px-4 py-3 bg-conversion text-conversion-foreground rounded-md font-cta text-sm hover:shadow-cta transition-all duration-300">
            Get Detailed ROI Report
          </button>
        </div>
      </div>

      <div className="mt-6 p-4 bg-surface rounded-lg">
        <p className="font-body text-xs text-muted-foreground text-center">
          * Calculations based on industry averages and 40% efficiency improvement. Actual results may vary based on implementation and usage patterns.
        </p>
      </div>
    </div>
  );
}